import { toFormData } from "utils";
import downloadFileAxios from "utils/download-file-axios";

import {
  AltiusRes,
  IEmail,
  IFile,
  IGetEmailFile,
  IMoveEmail,
  IPaginationResponse,
  ISendQuestionData,
  IUsersContact,
  Nullable,
  ReportFilters,
  ReportTableColumn
} from "../../types";
import {
  AccountForSwitch,
  BookmarkVisibility,
  IBookmark,
  IBookmarkType,
  IReportType,
  IUser,
  IUserModule,
  OnboardingConfigData,
  UserLight
} from "../../types/user";
import { DealAction, ReportsAction, UserAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IUserActions {
  updateUserAction: (data: {
    id: number;
    first_name?: string;
    last_name?: string;
    company_name?: string;
    phone_number?: string;
    email?: string;
    email_notification?: boolean;
    roles?: string;
    photo?: File;
  }) => Promise<void>;
  updateAvatarAction: (id: number, avatar: File | null) => Promise<void>;
  updateAccountLogoAction: (data: { accountId: number; logo: File }) => Promise<void>;
  getRolesAction: () => Promise<void>;
  getAudienceAction: () => Promise<void>;
  getUserAction: (params: { [key: string]: string | number }) => Promise<IPaginationResponse<IUser>>;
  getUsersContactAction: () => Promise<IUsersContact[]>;
  changeUserPasswordAction: (data: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }) => Promise<void>;
  getUserGptSettings: () => Promise<Required<IUserModule>["gptPrompt"]>;
  deleteUserGptSettings: () => Promise<void>;
  updateUserGptSettings: (data: IUserModule["gptPrompt"]) => Promise<void>;

  getEmailsAction: (data: {
    page: number;
    search?: string;
    order_by: "desc" | "asc";
    is_associated?: number;
    with_attachment?: number;
    deal_ids: number[];
    per_page: number;
  }) => Promise<AltiusRes<IPaginationResponse<IEmail>>>;
  downloadEmailFileAction: (data: IGetEmailFile) => Promise<void>;
  moveEmailAction: (email: IEmail, data: IMoveEmail) => Promise<IEmail>;
  // cancelMoveEmailAction: (email: IEmail) => Promise<void>;
  deleteEmailAction: (emailId: number) => Promise<void>;

  getBookmarksAction: () => Promise<void>;
  createBookmarkAction: (data: {
    name: string;
    params: string;
    type: IBookmarkType;
    is_default?: boolean;
    visibility?: BookmarkVisibility;
    shared_with?: number[];
    report_type?: "portfolio_analysis" | "comparison" | "deal_view";
  }) => Promise<void>;
  updateBookmarkAction: (data: {
    id: number;
    name?: string;
    is_default?: boolean;
    visibility?: BookmarkVisibility;
    shared_with?: number[];
    params?: string;
    report_type?: IReportType.PORTFOLIO | IReportType.COMPARISON | IReportType.DEAL_VIEW | IReportType.EXTERNAL | null;
    account_default?: boolean;
  }) => Promise<void>;
  deleteBookmarkAction: (id: number) => Promise<void>;
  setSelectedBookmark: (id: Nullable<IBookmark["id"]>) => void;
  resetPasswordReminderAction: () => Promise<void>;
  getPrivacyPolicyAction: () => Promise<Pick<IFile, "file_url">>;
  getTermsOfServiceAction: () => Promise<{ file_url: string }>;
  getManagersAction: () => Promise<void>;
  getUsersByRoleAction: (role_ids: number[]) => Promise<UserLight[]>;
  sendEmailWithQuestionAction: (data: ISendQuestionData) => Promise<void>;

  getAccountForSwitchAction: () => Promise<Array<AccountForSwitch>>;
  getAccountTokenAction: (accountId: number) => Promise<string>;
  hideUnlockFullAccessBannerAction: () => Promise<void>;

  upgradeAccountAction: (data: { domain: string; deal_id?: number }) => Promise<void>;

  updateNumberFormatAction: (payload: IUserModule["numberFormat"]) => Promise<void>;
  updateHideExtractorGuideAction: () => Promise<void>;
  setInvestorFilterAction: (payload: ReportFilters) => void;
  updateWorkspaceColumnsAction: (payload: IUserModule["workspaceColumns"]) => Promise<void>;
  updateWorkspaceFiltersAction: (payload: IUserModule["workspaceFilters"]) => Promise<void>;
  updateAmortizationTableColumnsAction: (payload: Record<string, boolean>) => Promise<void>;
  updateSnapshotTableColumnsAction: (columns: ReportTableColumn[]) => Promise<void>;
  updateHideWelcomePopupAction: () => Promise<void>;
  getOnboardingConfigAction: () => Promise<OnboardingConfigData>;
  submitOnboardingAction: (data: FormData) => Promise<void>;
  updateQuickViewSystemColsAction: (columns: string[]) => Promise<void>;
}

export class UserActions extends AsyncActions implements IUserActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public updateUserAction: IUserActions["updateUserAction"] = async (data) => {
    const { id, ...body } = data;
    const {
      data: { data: payload }
    } = await this.api.patch({
      url: `/api/v0.0.2/users/${id}`,
      data: body
    });
    this.dispatch({ type: UserAction.UpdateUserSuccess, payload });
  };

  public updateAvatarAction = async (id: number, avatar: File | null): Promise<void> => {
    const bodyFormData = new FormData();
    bodyFormData.append("photo", avatar || "");

    const {
      data: { data: payload }
    } = await this.api.post({
      url: `/api/v0.0.2/users/${id}?_method=PATCH`,
      data: bodyFormData,
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    this.dispatch({ type: UserAction.UpdateUserSuccess, payload });
  };

  public updateAccountLogoAction: IUserActions["updateAccountLogoAction"] = async (data) => {
    const { logo, accountId } = data;
    const bodyFormData = new FormData();
    bodyFormData.append("logo", logo);

    const {
      data: { data: payload }
    } = await this.api.post({
      url: `/api/v0.0.2/accounts/${accountId}/logo`,
      data: bodyFormData,
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });

    const account = {
      logo_url: payload.logo_url
    };
    this.dispatch({
      type: UserAction.UpdateUserSuccess,
      payload: { account }
    });
  };

  public getRolesAction = async (): Promise<void> => {
    const {
      data: { data: payload }
    } = await this.api.get({ url: "/api/v0.0.2/roles" });
    this.dispatch({ type: UserAction.GetRoleSuccess, payload });
  };

  public getAudienceAction = async (): Promise<void> => {
    const {
      data: { data: payload }
    } = await this.api.get({ url: "/api/v0.0.2/audiences" });
    this.dispatch({ type: UserAction.GetAudienceSuccess, payload });
  };

  public getUserAction: IUserActions["getUserAction"] = async (params: { [key: string]: string | number }) => {
    const {
      data: { data: payload }
    } = await this.api.get({
      url: "/api/v0.0.2/users",
      params
    });
    return {
      data: payload.data,
      total: payload.total,
      last_page: payload.last_page
    };
  };

  public getUsersContactAction: IUserActions["getUsersContactAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IUsersContact[]>>({ url: "/api/v0.0.3/shares/users" });
    return data;
  };

  public getUserGptSettings: Required<IUserActions>["getUserGptSettings"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Required<IUserModule>["gptPrompt"]>>({ url: "/api/v0.0.3/user/gpt-settings" });
    return data;
  };

  public deleteUserGptSettings = async () => {
    await this.api.delete({ url: "/api/v0.0.3/user/gpt-settings" });
  };

  public updateUserGptSettings: IUserActions["updateUserGptSettings"] = async (data: IUserModule["gptPrompt"]) => {
    await this.api.patch({ url: "/api/v0.0.3/user/gpt-settings", data });
  };

  public changeUserPasswordAction: IUserActions["changeUserPasswordAction"] = async (data) => {
    await this.api.post({ url: "/api/v0.0.3/user/update-password", data });
  };

  public getBookmarksAction = async (): Promise<void> => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IBookmark[]>>({
      // url: "/reports/templates",
      url: "/api/v0.0.3/bookmarks"
    });
    this.dispatch({
      type: UserAction.GetBookmarksSuccess,
      payload: data
    });
  };

  public moveEmailAction: IUserActions["moveEmailAction"] = async (email, data) => {
    await this.api.post({
      url: `/api/v0.0.3/emails/${email.id}/move`,
      data: {
        ...data,
        extract_with_ai: data.extract_with_ai || false
      }
    });
    // this.dispatch({
    //   type: UserAction.MoveEmailSuccess,
    //   payload: { emailId: email.id }
    // });
    const newEmail = { ...email, ...data };
    this.dispatch({
      type: DealAction.HandleEmailSuccess,
      payload: { item: newEmail, event: "add" }
    });
    return newEmail;
  };

  public deleteEmailAction: IUserActions["deleteEmailAction"] = async (emailId) => {
    await this.api.delete({ url: `/api/v0.0.3/emails/${emailId}` });
  };

  public getEmailsAction: IUserActions["getEmailsAction"] = async (payload) => {
    const { data } = await this.api.post<AltiusRes<IPaginationResponse<IEmail>>>({
      url: "/api/v0.0.3/incoming-emails",
      data: payload
    });
    // this.dispatch({
    //   type: UserAction.GetIncomingEmailsSuccess,
    //   payload: data.data.data
    // });

    return data;
  };

  public downloadEmailFileAction: IUserActions["downloadEmailFileAction"] = async (data) => {
    const { emailId, fileId } = data;
    // Get Response from API
    return this.api
      .get({
        url: `/api/v0.0.3/incoming-emails/${emailId}/file/${fileId}`,
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public createBookmarkAction: IUserActions["createBookmarkAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<IBookmark>>({
      // url: "/reports/template",
      url: "/api/v0.0.3/bookmarks",
      data
    });

    this.dispatch({
      type: UserAction.CreateBookmarkSuccess,
      payload
    });
  };

  public updateBookmarkAction: IUserActions["updateBookmarkAction"] = async ({ id, ...body }) => {
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<IBookmark>>({
      url: `/api/v0.0.3/bookmarks/${id}`,
      data: body
    });

    this.dispatch({
      type: UserAction.UpdateBookmarkSuccess,
      payload
    });
  };

  public deleteBookmarkAction = async (id: number): Promise<void> => {
    await this.api.delete({
      url: `/api/v0.0.3/bookmarks/${id}`
    });

    this.dispatch({
      type: UserAction.DeleteBookmarkSuccess,
      payload: id
    });
  };

  public resetPasswordReminderAction = async (): Promise<void> => {
    await this.api.put({ url: "/api/v0.0.2/users/password-reminder" });
  };

  public getPrivacyPolicyAction = async (): Promise<Pick<IFile, "file_url">> => {
    const {
      data: { data: response }
    } = await this.api.get<AltiusRes<Pick<IFile, "file_url">>>({
      url: "/api/v0.0.3/privacy-policy"
    });
    return response;
  };

  public getTermsOfServiceAction = async (): Promise<{ file_url: string }> => {
    const {
      data: { data: response }
    } = await this.api.get<AltiusRes<{ file_url: string }>>({
      url: "/api/v0.0.3/terms-of-service"
    });
    return response;
  };

  public getManagersAction = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<UserLight[]>>({
      url: "/api/v0.0.3/user"
    });
    this.dispatch({
      type: UserAction.GetManagersSuccess,
      payload: data
    });
  };

  public getUsersByRoleAction = async (role_ids: number[]) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<UserLight[]>>({
      url: "/api/v0.0.3/user/",
      params: {
        role_id: role_ids
      }
    });
    return data;
  };

  public sendEmailWithQuestionAction = async (data: ISendQuestionData) => {
    await this.api.post({
      url: "/api/v0.0.3/profile/request",
      data: toFormData({ ...data }),
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  };

  public setSelectedBookmark: IUserActions["setSelectedBookmark"] = (payload) => {
    this.dispatch({ type: UserAction.SetSelectedBookmark, payload });
  };

  public getAccountForSwitchAction: IUserActions["getAccountForSwitchAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<AccountForSwitch[]>>({ url: "/api/v0.0.3/user-accounts" });
    return data;
  };

  public getAccountTokenAction: IUserActions["getAccountTokenAction"] = async (accountId) => {
    const {
      data: {
        data: { token }
      }
    } = await this.api.get<AltiusRes<{ token: string }>>({ url: `/api/v0.0.3/user-account/${accountId}/token` });
    return token;
  };

  public hideUnlockFullAccessBannerAction: IUserActions["hideUnlockFullAccessBannerAction"] = async () => {
    await this.api.post({ url: "/api/v0.0.3/profile/hide-unlock-full-access-banner" });
    this.dispatch({ type: UserAction.SetShowUnlockFullAccess, payload: false });
  };

  public upgradeAccountAction: IUserActions["upgradeAccountAction"] = async (data) => {
    await this.api.post({
      url: "/api/v0.0.3/upgrade-account",
      data
    });
  };

  public updateNumberFormatAction = async (data: IUserModule["numberFormat"]) => {
    await this.api.post({
      url: "/api/v0.0.3/user/settings",
      data: { type: "numberFormat", key: "numberFormat", data }
    });
    this.dispatch({ type: UserAction.SetNumberFormat, payload: data });
  };

  public updateHideExtractorGuideAction = async () => {
    await this.api.post({
      url: "/api/v0.0.3/user/settings",
      data: { type: "hideExtractorGuide", key: "hideExtractorGuide", data: true }
    });
    this.dispatch({ type: UserAction.SetHideExtractorGuide, payload: true });
  };

  public updateHideWelcomePopupAction: IUserActions["updateHideWelcomePopupAction"] = async () => {
    await this.api.post({
      url: "/api/v0.0.3/user/settings",
      data: { type: "hideWelcomePopup", key: "hideWelcomePopup", data: true }
    });
    this.dispatch({ type: UserAction.SetHideWelcomePopup, payload: true });
  };

  public updateWorkspaceColumnsAction: IUserActions["updateWorkspaceColumnsAction"] = async (data) => {
    await this.api.post<AltiusRes<string[]>>({
      url: "/api/v0.0.3/user/settings",
      data: {
        key: "workspaceColumns",
        type: "workspaceColumns",
        data
      }
    });

    this.dispatch({
      type: UserAction.UpdateWorkspaceColumns,
      payload: data
    });
  };

  public updateWorkspaceFiltersAction: IUserActions["updateWorkspaceFiltersAction"] = async (data) => {
    await this.api.post({
      url: "/api/v0.0.3/user/settings",
      data: {
        key: "workspaceFilters",
        type: "workspaceFilters",
        data
      }
    });

    this.dispatch({
      type: UserAction.UpdateWorkspaceFilters,
      payload: data
    });
  };

  public updateAmortizationTableColumnsAction: IUserActions["updateAmortizationTableColumnsAction"] = async (data) => {
    await this.api.post<AltiusRes<string[]>>({
      url: "/api/v0.0.3/user/settings",
      data: {
        key: "amortizationColumns",
        type: "amortizationColumns",
        data
      }
    });

    this.dispatch({
      type: UserAction.UpdateAmortizationTableColumns,
      payload: data
    });
  };

  public updateSnapshotTableColumnsAction = async (columns: ReportTableColumn[]) => {
    await this.api.post({
      url: "/api/v0.0.3/user/settings",
      data: { type: "snapshot", key: "snapshot", data: columns }
    });
  };

  public setInvestorFilterAction = (filters: ReportFilters): void => {
    this.dispatch({ type: ReportsAction.SetQueries, payload: { filters } });
    this.dispatch({
      type: DealAction.UpdateQueries,
      payload: { queries: { filters }, isFullReplace: false }
    });
  };

  public getOnboardingConfigAction: IUserActions["getOnboardingConfigAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get({ url: "/api/v0.0.3/onboarding" });
    return data;
  };

  public submitOnboardingAction: IUserActions["submitOnboardingAction"] = async (data) => {
    const isFormData = data instanceof FormData;
    const { status } = await this.api.post({
      url: "/api/v0.0.3/onboarding",
      data,
      ...(isFormData && { headers: { "Content-Type": "multipart/form-data" } })
    });

    if (status >= 200) {
      this.dispatch({ type: UserAction.SetOnboardingCompleted, payload: true });
    }
  };

  public updateQuickViewSystemColsAction: IUserActions["updateQuickViewSystemColsAction"] = async (columns) => {
    await this.api.post({
      url: "/api/v0.0.3/user/settings",
      data: { type: "quickViewSystemCols", data: columns }
    });
    this.dispatch({
      type: ReportsAction.SetQuickViewQueries,
      payload: {
        queries: {
          columns: { visibleSystemColumns: columns }
        }
      }
    });
  };
}
